



// Firebase Cloud Messaging utilities for push notifications
// NOTE: This version supports FOREGROUND notifications only (when app is open)
// For background notifications, a service worker file must be manually created
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { firebaseApp } from 'app';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { firestore } from 'app';

let messaging: Messaging | null = null;

// VAPID key from Firebase Console
const VAPID_KEY = 'BPP18YqeaHtMq4xLXfYgotbh7__8ZEo1YXCaBE5oRh_Dv3Penm4MoHvheaiZHR4qxG1-9ra1RGZBvQjncOwiIkU';

// Service worker URL - served from public directory
const SERVICE_WORKER_URL = '/firebase-messaging-sw.js';

// Initialize messaging
const initMessaging = (): Messaging | null => {
  if (messaging) return messaging;
  
  try {
    messaging = getMessaging(firebaseApp);
    console.log('[FCM] ✅ Messaging initialized');
    return messaging;
  } catch (error) {
    console.error('[FCM] ❌ Failed to initialize messaging:', error);
    return null;
  }
};

// Export for external use
export const initializeMessaging = (): Messaging | null => {
  return initMessaging();
};

// Check if running in a native app wrapper (like Natively)
export const isNativeApp = (): boolean => {
  // Check for common native app indicators
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = ('standalone' in window.navigator) && (window.navigator as any).standalone;
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for Natively user agent
  const isNatively = /Natively/i.test(userAgent);
  
  // Check if running as installed PWA or native wrapper
  const isNative = isStandalone || isIOSStandalone || isNatively;
  
  console.log('[FCM] Environment check:', {
    isStandalone,
    isIOSStandalone,
    isNatively,
    isNative
  });
  
  return isNative;
};

// Check if BuildNatively native notification bridge is available
export const hasBuildNativelyBridge = (): boolean => {
  // BuildNatively may expose notifications through webkit messageHandlers or a global object
  const hasWebKit = !!(window as any).webkit?.messageHandlers?.natively;
  const hasNativelyGlobal = !!(window as any).natively;
  
  console.log('[FCM] BuildNatively bridge check:', {
    hasWebKit,
    hasNativelyGlobal,
    available: hasWebKit || hasNativelyGlobal
  });
  
  return hasWebKit || hasNativelyGlobal;
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    // Skip browser notification prompt if running in native app
    if (isNativeApp()) {
      console.log('[FCM] ✅ Running in native app - skipping browser notification permission');
      // Return true to indicate we should proceed with FCM setup
      // The native wrapper will handle actual notification permissions
      return true;
    }
    
    console.log('[FCM] Requesting browser notification permission...');
    
    // Check if Notification API is available
    if (!('Notification' in window)) {
      console.error('[FCM] ❌ Notification API not available');
      return false;
    }
    
    // Check current permission status
    const currentPermission = Notification.permission;
    console.log('[FCM] Current permission status:', currentPermission);
    
    // If already granted, return true
    if (currentPermission === 'granted') {
      console.log('[FCM] ✅ Permission already granted');
      return true;
    }
    
    // If denied, cannot request again
    if (currentPermission === 'denied') {
      console.error('[FCM] ❌ Permission previously denied');
      return false;
    }
    
    // Request permission from browser
    console.log('[FCM] Prompting user for notification permission...');
    const permission = await Notification.requestPermission();
    console.log('[FCM] Permission result:', permission);
    
    return permission === 'granted';
  } catch (error) {
    console.error('[FCM] ❌ Error requesting notification permission:', error);
    return false;
  }
};

// Get FCM token for this device
export const getFCMToken = async (): Promise<string | null> => {
  console.log('[FCM] ==== STARTING FCM TOKEN RETRIEVAL ====');
  
  // Check if BuildNatively native bridge is available
  if (isNativeApp()) {
    console.log('[FCM] ✅ Native app detected - attempting to get FCM token from Firebase SDK');
    console.log('[FCM] BuildNatively should handle native notification permissions');
    
    // BuildNatively integrates Firebase natively, so we can still use Firebase SDK
    // but without service worker requirement
    try {
      const msg = initMessaging();
      if (!msg) {
        console.error('[FCM] ❌ Messaging initialization failed');
        return null;
      }
      
      // Try to get token WITHOUT service worker (BuildNatively handles it natively)
      console.log('[FCM] Requesting FCM token (native mode)...');
      const currentToken = await getToken(msg, { 
        vapidKey: VAPID_KEY
      });
      
      if (currentToken) {
        console.log('[FCM] ✅ FCM token received from native app');
        console.log('[FCM] Token (first 50 chars):', currentToken.substring(0, 50) + '...');
        return currentToken;
      }
      
      console.warn('[FCM] ⚠️ No FCM token received from native app');
      console.warn('[FCM] Make sure Firebase is properly configured in BuildNatively');
      return null;
    } catch (error) {
      console.error('[FCM] ❌ Error getting FCM token from native app:', error);
      return null;
    }
  }
  
  // Check if running in native app that doesn't support service workers
  if (isNativeApp() && !('serviceWorker' in navigator)) {
    console.warn('[FCM] ⚠️ Native app detected without service worker support');
    console.warn('[FCM] Web-based push notifications are not supported in this native wrapper');
    console.warn('[FCM] Please enable Firebase notifications in BuildNatively dashboard');
    return null;
  }
  
  try {
    const msg = initMessaging();
    if (!msg) {
      console.error('[FCM] ❌ Messaging initialization failed');
      return null;
    }

    // Skip browser permission check - FCM handles it internally
    console.log('[FCM] ℹ️ Proceeding with FCM token registration (no permission check)');

    // Register service worker from static assets
    if ('serviceWorker' in navigator) {
      let registration;
      try {
        console.log('[FCM] Checking service worker registration...');
        
        // Check if already registered
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        
        if (!existingRegistration) {
          // Register service worker from static assets
          console.log('[FCM] Registering service worker from:', SERVICE_WORKER_URL);
          registration = await navigator.serviceWorker.register(
            SERVICE_WORKER_URL,
            { scope: '/', type: 'classic' }
          );
          console.log('[FCM] ✅ Service worker registered successfully');
          console.log('[FCM]    - Scope:', registration.scope);
          console.log('[FCM]    - Active:', registration.active);
          
          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;
          console.log('[FCM] ✅ Service worker ready');
        } else {
          console.log('[FCM] ✅ Service worker already registered');
          console.log('[FCM]    - Scope:', existingRegistration.scope);
          console.log('[FCM]    - Active:', existingRegistration.active);
          registration = existingRegistration;
          await navigator.serviceWorker.ready;
        }
      } catch (swError: any) {
        console.error('[FCM] ❌ Service worker registration failed:', swError);
        console.error('[FCM] Error details:', {
          message: swError.message,
          name: swError.name,
          stack: swError.stack
        });
        return null;
      }

      // Try to get token WITH service worker registration
      console.log('[FCM] Requesting FCM token with VAPID key...');
      console.log('[FCM] VAPID key (first 20 chars):', VAPID_KEY.substring(0, 20) + '...');
      
      const currentToken = await getToken(msg, { 
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration 
      });
      
      if (currentToken) {
        console.log('[FCM] ✅ FCM token received successfully');
        console.log('[FCM] Token (first 50 chars):', currentToken.substring(0, 50) + '...');
        return currentToken;
      }
      
      console.log('[FCM] ⚠️ No registration token available');
      console.log('[FCM] This could mean:');
      console.log('[FCM]   - User denied permission');
      console.log('[FCM]   - Service worker failed to activate');
      console.log('[FCM]   - VAPID key mismatch');
      console.log('[FCM]   - Domain not authorized in Firebase Console');
      return null;
    } else {
      console.error('[FCM] ❌ Service workers not supported in this browser');
      return null;
    }
  } catch (error: any) {
    console.error('[FCM] ❌ Error getting FCM token:', error);
    console.error('[FCM] Error details:', {
      message: error.message,
      code: error.code,
      name: error.name,
      stack: error.stack
    });
    return null;
  }
};

// Save FCM token to Firestore for this user.
// Tracks a stable per-browser device id (webDeviceTokens map) so that when
// THIS browser re-registers with a new token, its old token is auto-pruned
// instead of accumulating and causing duplicate notifications.
const WEB_DEVICE_KEY = 'tap_web_device_id';
const getWebDeviceId = (): string => {
  let id = localStorage.getItem(WEB_DEVICE_KEY);
  if (!id) {
    id = `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(WEB_DEVICE_KEY, id);
  }
  return id;
};

// Browsers can't expose the computer's real name (privacy), but we can
// label the registration "Chrome on Windows" etc. so admins can tell
// web devices apart on the Push Tokens report (like native phone names).
const getBrowserDeviceLabel = (): { label: string; browser: string; os: string } => {
  const ua = navigator.userAgent || '';
  let browser = 'Browser';
  if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/opr\/|opera/i.test(ua)) browser = 'Opera';
  else if (/samsungbrowser/i.test(ua)) browser = 'Samsung Internet';
  else if (/chrome|crios/i.test(ua)) browser = 'Chrome';
  else if (/firefox|fxios/i.test(ua)) browser = 'Firefox';
  else if (/safari/i.test(ua)) browser = 'Safari';
  let os = 'Unknown OS';
  if (/windows/i.test(ua)) os = 'Windows';
  else if (/iphone|ipod/i.test(ua)) os = 'iPhone';
  else if (/ipad/i.test(ua)) os = 'iPad';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/mac os x|macintosh/i.test(ua)) os = 'Mac';
  else if (/cros/i.test(ua)) os = 'ChromeOS';
  else if (/linux/i.test(ua)) os = 'Linux';
  const isMobile = /mobile|iphone|ipod|android.*mobile/i.test(ua);
  const label = `${browser} on ${os}${isMobile ? ' (mobile)' : ''}`;
  return { label, browser, os };
};

export const saveFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    console.log('[FCM] Saving token to Firestore for user:', userId);
    const userRef = doc(firestore, 'users', userId);
    
    // Get existing user data
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data() || {};
    
    const deviceId = getWebDeviceId();
    const webDeviceTokens: Record<string, string> = { ...(userData.webDeviceTokens || {}) };
    const previousToken = webDeviceTokens[deviceId];

    let fcmTokens: string[] = userData.fcmTokens || [];

    // Auto-prune: this browser re-registered with a new token — drop its old one
    if (previousToken && previousToken !== token) {
      fcmTokens = fcmTokens.filter((t: string) => t !== previousToken);
      console.log('[FCM] 🔄 Replaced this browser\'s previous token (auto-pruned)');
    }

    const changed = previousToken !== token || !fcmTokens.includes(token);
    if (!fcmTokens.includes(token)) fcmTokens = [...fcmTokens, token];
    webDeviceTokens[deviceId] = token;

    // Device label — refreshed on every registration so the report's
    // "seen" date stays current even when the token is unchanged.
    const { label, browser, os } = getBrowserDeviceLabel();
    const webDeviceInfo: Record<string, any> = { ...(userData.webDeviceInfo || {}) };
    // Drop info rows for browsers whose token no longer exists.
    Object.keys(webDeviceInfo).forEach((k) => {
      if (!(k in webDeviceTokens)) delete webDeviceInfo[k];
    });
    webDeviceInfo[deviceId] = { label, browser, os, lastSeen: new Date().toISOString() };

    if (changed) {
      await setDoc(userRef, {
        fcmTokens,
        webDeviceTokens,
        webDeviceInfo,
        lastTokenUpdate: new Date().toISOString()
      }, { merge: true });
      
      console.log('[FCM] ✅ FCM token saved to Firestore');
      console.log('[FCM]    - User ID:', userId);
      console.log('[FCM]    - Total tokens:', fcmTokens.length);
      console.log('[FCM]    - Device label:', label);
    } else {
      // Token unchanged — still refresh the device label / lastSeen.
      await setDoc(userRef, { webDeviceInfo }, { merge: true });
      console.log('[FCM] ℹ️ Token already exists in Firestore (device label refreshed)');
    }
  } catch (error) {
    console.error('[FCM] ❌ Error saving FCM token:', error);
    throw error;
  }
};

// Remove FCM token from Firestore (on logout or token refresh)
export const removeFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    console.log('[FCM] Removing token from Firestore for user:', userId);
    const userRef = doc(firestore, 'users', userId);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data() || {};
    
    const fcmTokens = (userData.fcmTokens || []).filter((t: string) => t !== token);
    
    await setDoc(userRef, {
      fcmTokens
    }, { merge: true });
    
    console.log('[FCM] ✅ FCM token removed from Firestore');
  } catch (error) {
    console.error('[FCM] ❌ Error removing FCM token:', error);
  }
};

// Setup foreground message listener
export const setupForegroundMessageListener = (callback: (payload: any) => void) => {
  const messagingInstance = initMessaging();
  if (!messagingInstance) return () => {};
  
  console.log('[FCM] Setting up foreground message listener');
  
  const unsubscribe = onMessage(messagingInstance, (payload) => {
    console.log('[FCM] 📬 Foreground message received:', payload);
    
    // Try to show browser notification for foreground messages
    // Only if we have permission
    if (Notification.permission === 'granted') {
      try {
        const notificationTitle = payload.notification?.title || 'TAP Konnect';
        const notificationOptions = {
          body: payload.notification?.body || '',
          icon: payload.notification?.icon || '/logo.png',
          badge: '/badge.png',
          tag: payload.data?.tag || 'default',
          data: payload.data || {},
          requireInteraction: payload.data?.priority === 'high'
        };
        
        console.log('[FCM] 🔔 Showing notification:', notificationTitle);
        const notification = new Notification(notificationTitle, notificationOptions);
        
        // Handle notification click
        notification.onclick = () => {
          const urlToOpen = payload.data?.url || '/';
          window.focus();
          // For native apps and SPAs, prefer in-app navigation over full page reload
          // Check if running in native app or can use History API
          if (window.history && window.history.pushState) {
            // Dispatch a custom event that the router can listen to
            window.dispatchEvent(new CustomEvent('notification-navigate', { 
              detail: { url: urlToOpen } 
            }));
          } else {
            // Fallback to full page navigation
            window.location.href = urlToOpen;
          }
          notification.close();
        };
      } catch (error) {
        console.error('[FCM] ❌ Failed to show notification:', error);
      }
    } else {
      console.warn('[FCM] ⚠️ Cannot show notification - permission not granted:', Notification.permission);
    }
    
    callback(payload);
  });
  
  return unsubscribe;
};

// Get current notification permission status
export const getNotificationPermissionStatus = (): NotificationPermission => {
  // If running in native app, assume granted (native handles permissions)
  if (isNativeApp()) {
    console.log('[FCM] ℹ️ Native app detected, returning granted permission');
    return 'granted';
  }
  
  if (!('Notification' in window)) {
    console.log('[FCM] ℹ️ Notification API not available, returning denied');
    return 'denied';
  }
  
  const permission = Notification.permission;
  console.log('[FCM] ℹ️ Current notification permission:', permission);
  return permission;
};

// Check if notifications are supported
export const areNotificationsSupported = (): boolean => {
  // If running in native app, notifications are supported via native layer
  if (isNativeApp()) {
    console.log('[FCM] Notification support check: true (native app)');
    return true;
  }
  
  const supported = 'Notification' in window && 'serviceWorker' in navigator;
  console.log('[FCM] Notification support check:', supported);
  return supported;
};
